import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import { useSensors, useFleetSummary } from '../hooks/useSensors';
import {
  SensorsTable,
  COLUMNS,
  loadColumnVisibility,
  saveColumnVisibility,
} from '../components/SensorsTable';
import { InactiveSensorsSummary } from '@/features/operations-inactive-sensors/components/InactiveSensorsSummary';
import { EditSensorDialog } from '../components/EditSensorDialog';
import { CreateSensorDialog } from '../components/CreateSensorDialog';
import ReassignDialog from '@/features/accounts/components/ReassignDialog';
import { exportToCSV, copyToClipboard } from '../utils/csvExport';
import type { Sensor, SensorsFilters } from '../types';

// =============================================================================
// localStorage Keys
// =============================================================================

const FILTER_STORAGE_KEY = 'atek-sensors-column-filters';

// =============================================================================
// Page Component
// =============================================================================

export default function SensorsPage() {
  // ---------------------------------------------------------------------------
  // Column filter state (persisted to localStorage)
  // ---------------------------------------------------------------------------
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => {
      try {
        return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || '{}');
      } catch {
        return {};
      }
    },
  );

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ---------------------------------------------------------------------------
  // Column visibility state (persisted to localStorage)
  // ---------------------------------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  // ---------------------------------------------------------------------------
  // Sort state
  // ---------------------------------------------------------------------------
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(key === 'timestamp' ? -1 : 1);
    }
  };

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------
  const [page, setPage] = useState(0);
  const pageLength = 50;

  // ---------------------------------------------------------------------------
  // Extract server-side text filters
  // ---------------------------------------------------------------------------
  const textServerFilters = useMemo(
    () => ({
      name: columnFilters['name'] || '',
      siteLocation: columnFilters['site'] || '',
      streamName: columnFilters['stream'] || '',
      currentValue: columnFilters['value'] || '',
      sensorTypeName: columnFilters['sensorType'] || '',
      usergroupName: columnFilters['usergroup'] || '',
      customId: columnFilters['customId'] || '',
      inputNumber: columnFilters['inputNumber'] || '',
      tags: columnFilters['tags'] || '',
    }),
    [columnFilters],
  );
  const debouncedTextServerFilters = useDebounce(textServerFilters, 300);

  // Extract status filter from column filters (stabilized as JSON string for dep comparison)
  const statusFilterRaw = columnFilters['status'] || '';
  const statusFilter: number[] = useMemo(() => {
    try {
      if (!statusFilterRaw) return [];
      return JSON.parse(statusFilterRaw).map(Number);
    } catch {
      return [];
    }
  }, [statusFilterRaw]);

  // Extract billable filter (stabilized as raw string for dep comparison)
  const billableFilterRaw = columnFilters['billable'] || '';
  const billableFilter = useMemo(() => {
    try {
      if (!billableFilterRaw) return undefined;
      const selected = JSON.parse(billableFilterRaw) as string[];
      if (selected.length === 1) return selected[0] === 'true';
      return undefined;
    } catch {
      return undefined;
    }
  }, [billableFilterRaw]);

  // Extract account filter (stabilized as raw string for dep comparison)
  const accountFilterRaw = columnFilters['account'] || '';
  const selectedAccountId = useMemo(() => {
    if (!accountFilterRaw || accountFilterRaw === 'all') return 'all';
    if (accountFilterRaw.trim().startsWith('[')) return 'all';
    return accountFilterRaw;
  }, [accountFilterRaw]);

  // Extract image filter (stabilized as raw string for dep comparison)
  const imageFilterRaw = columnFilters['image'] || '';
  const hasImageFilter = useMemo(() => {
    try {
      if (!imageFilterRaw) return undefined;
      const selected = JSON.parse(imageFilterRaw) as string[];
      if (selected.length === 1) return selected[0] === 'with';
      return undefined;
    } catch {
      return undefined;
    }
  }, [imageFilterRaw]);

  const lastReadingRaw = columnFilters['lastReading'] || '';
  const [lastReadingFrom = '', lastReadingTo = ''] = lastReadingRaw.split('>>');

  // Reset page when server-side filters or sorting change
  useEffect(() => {
    setPage(0);
  }, [
    debouncedTextServerFilters,
    statusFilterRaw,
    billableFilterRaw,
    accountFilterRaw,
    imageFilterRaw,
    lastReadingRaw,
    sortKey,
    sortDirection,
  ]);

  // ---------------------------------------------------------------------------
  // Sensor detail sheet
  // ---------------------------------------------------------------------------
  const [editSensorId, setEditSensorId] = useState<string | null>(null);
  const [reassignSensor, setReassignSensor] = useState<Sensor | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // ---------------------------------------------------------------------------
  // API queries
  // ---------------------------------------------------------------------------
  const filters: SensorsFilters = {
    name: debouncedTextServerFilters.name || undefined,
    siteLocation: debouncedTextServerFilters.siteLocation || undefined,
    streamName: debouncedTextServerFilters.streamName || undefined,
    currentValue: debouncedTextServerFilters.currentValue || undefined,
    sensorTypeName: debouncedTextServerFilters.sensorTypeName || undefined,
    usergroupName: debouncedTextServerFilters.usergroupName || undefined,
    customId: debouncedTextServerFilters.customId || undefined,
    inputNumber: debouncedTextServerFilters.inputNumber || undefined,
    tags: debouncedTextServerFilters.tags || undefined,
    lastReadingFrom: lastReadingFrom || undefined,
    lastReadingTo: lastReadingTo || undefined,
    status_in: statusFilter.length > 0 ? statusFilter : undefined,
    accountId: selectedAccountId !== 'all' ? selectedAccountId : undefined,
    billable: billableFilter,
    hasImage: hasImageFilter,
    pageNumber: page,
    pageLength: pageLength,
    sortKey: sortKey,
    sortDirection: sortDirection,
  };

  const sensorsQuery = useSensors(filters);
  const fleetSummaryQuery = useFleetSummary();

  const sensors = sensorsQuery.data?.sensors || [];
  const total = sensorsQuery.data?.total || 0;
  const totalPages = Math.ceil(total / pageLength);

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const handleExportCSV = () => {
    exportToCSV(sensors, COLUMNS, 'sensors');
    toast.success(`Exported ${sensors.length} rows`);
  };

  const handleCopyClipboard = () => {
    copyToClipboard(sensors, COLUMNS);
    toast.success('Copied to clipboard');
  };

  const handleClearColumnFilters = () => {
    setColumnFilters({});
    setPage(0);
  };

  // ---------------------------------------------------------------------------
  // Refresh
  // ---------------------------------------------------------------------------
  const handleRefresh = () => {
    sensorsQuery.refetch();
    fleetSummaryQuery.refetch();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            All Sensors{' '}
            <small>Full sensor fleet management</small>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Sensor
          </Button>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={sensorsQuery.isFetching}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${sensorsQuery.isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Fleet Summary Cards */}
      <InactiveSensorsSummary
        summary={fleetSummaryQuery.data}
        isLoading={fleetSummaryQuery.isLoading}
      />

      {/* Table */}
      {sensorsQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">
              Error loading sensors. Please try again.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleRefresh}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <SensorsTable
          sensors={sensors}
          pageCount={sensors.length}
          isLoading={sensorsQuery.isLoading}
          total={total}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onEditSensor={setEditSensorId}
          onReassignSensor={setReassignSensor}
          columnFilters={columnFilters}
          setColumnFilter={setColumnFilter}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onExportCSV={handleExportCSV}
          onCopyClipboard={handleCopyClipboard}
          onClearColumnFilters={handleClearColumnFilters}
        />
      )}

      {/* Create Sensor Dialog */}
      <CreateSensorDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      {/* Edit Sensor Dialog */}
      <EditSensorDialog
        sensorId={editSensorId}
        open={!!editSensorId}
        onOpenChange={(open) => {
          if (!open) setEditSensorId(null);
        }}
      />

      {/* Reassign Sensor Dialog */}
      <ReassignDialog
        entityType="sensor"
        entityId={reassignSensor?._id || ''}
        entityLabel={reassignSensor?.name ?? ''}
        currentAccountId={
          reassignSensor?.organisation && typeof reassignSensor.organisation === 'object'
            ? reassignSensor.organisation._id
            : (typeof reassignSensor?.organisation === 'string' ? reassignSensor.organisation : '')
        }
        currentAccountName={
          reassignSensor?.organisation && typeof reassignSensor.organisation === 'object'
            ? reassignSensor.organisation.name
            : ''
        }
        open={!!reassignSensor}
        onOpenChange={(open) => { if (!open) setReassignSensor(null); }}
      />
    </div>
  );
}
